import isPropValid from '@emotion/is-prop-valid';

/**
 * Global styled-components v6 prop-forwarding policy for Kyoko and Ava apps.
 *
 * styled-components v6 no longer ships the old default DOM prop validator. Without
 * an explicit `shouldForwardProp`, style-only props such as `variant`, `size`, or
 * `isSelected` can leak onto rendered DOM nodes and trigger React warnings.
 *
 * This helper keeps valid HTML/SVG attributes when the styled target is an
 * intrinsic element (`styled.div`, `styled.button`, etc.) by delegating to
 * `@emotion/is-prop-valid`. It intentionally forwards all props for custom
 * component targets (`styled(Link)`, `styled(Box)`, etc.) so those components can
 * receive their public props.
 *
 * Use this at app/test roots via `StyleSheetManager shouldForwardProp`.
 * Prefer transient props (`$isOpen`) for new local style-only props.
 *
 * Read more:
 * - styled-components StyleSheetManager / shouldForwardProp:
 *   https://styled-components.com/docs/api#stylesheetmanager
 * - styled-components transient props:
 *   https://styled-components.com/docs/api#transient-props
 * - @emotion/is-prop-valid:
 *   https://github.com/emotion-js/emotion/tree/main/packages/is-prop-valid
 */
export function shouldForwardStyledProp(propName: string, target: unknown) {
  return typeof target === 'string' ? isPropValid(propName) : true;
}

/**
 * Creates a component-specific `shouldForwardProp` for styled components that
 * have known style-only props.
 *
 * It first blocks every prop in `filteredProps`, then applies the global
 * `shouldForwardStyledProp` policy. This is most useful for shared Kyoko
 * primitives and styled custom-component targets where transient props are not a
 * good fit, for example `styled(Link)` wrappers that consume `variant` or `size`
 * for styling but must not pass those props to the underlying react component.
 *
 * Keep these deny-lists narrow: include only props consumed by the styled
 * interpolation or wrapper styles. Higher-level/render-only props should usually
 * be destructured before they reach the styled DOM boundary.
 */
export function createShouldForwardStyledProp(filteredProps: ReadonlySet<string> | readonly string[]) {
  const filteredPropSet = new Set(filteredProps);

  return (propName: string, target: unknown) =>
    !filteredPropSet.has(propName) && shouldForwardStyledProp(propName, target);
}
